import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18next from "i18next";
import { SliderWidgetScene } from "@learningblocks/widgets/slider-widget-components";

import className from "./style.module.scss";

// Fetch and parse a JSON resource.
const fetchJson = async (url) => {
  const response = await fetch(url);
  // fetch() does not reject on HTTP 4xx/5xx — surface a non-OK status as an error
  // so the localized fallback and outer catch trigger on status rather than on
  // whether the error body happens to be unparseable JSON.
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
  return response.json();
};

// Fetch a localized resource for the active language, falling back to English
// when the localized file is unavailable.
const fetchLocalizedJson = async (baseUrl, lang, fileName) => {
  try {
    return await fetchJson(`${baseUrl}locales/${lang}/${fileName}`);
  } catch {
    return fetchJson(`${baseUrl}locales/en/${fileName}`);
  }
};

/**
 * Slider widget for image-hotspot simulations.
 *
 * Wraps the shared `SliderWidgetScene` but, unlike the scrollable slider widget
 * (`scrollable-components/.../Slider-Widget`), renders it without the
 * standalone "card" chrome (white background, border-radius and drop-shadow).
 * In image-hotspots the slider is already presented inside the widget modal, so
 * that extra card produced a confusing "modal inside a modal". This component
 * keeps the slider flush within the surrounding modal.
 */
export function SliderWidget(props) {
  const lang = i18next.language || "en";
  const { name } = props.data;

  const [sceneData, setSceneData] = useState(null);
  const [animationData, setAnimationData] = useState(null);
  const [mobileAnimationData, setMobileAnimationData] = useState(null);
  const [translationData, setTranslationData] = useState(null);

  useEffect(() => {
    let isActive = true;
    const baseUrl = window.CONTENT_BASE_URL;

    const loadData = async () => {
      try {
        const [scene, animation, translation] = await Promise.all([
          fetchJson(`${baseUrl}sliderScene-${name}.json`),
          fetchLocalizedJson(baseUrl, lang, `sliderAnimation-${name}.json`),
          fetchLocalizedJson(baseUrl, lang, `sliderTranslation-${name}.json`),
        ]);
        // Mobile animation is optional; render without it when absent.
        const mobile = await fetchLocalizedJson(
          baseUrl,
          lang,
          `sliderAnimation-${name}_mobile.json`
        ).catch(() => null);

        if (!isActive) return;
        setSceneData(scene);
        setAnimationData(animation);
        setTranslationData(translation);
        setMobileAnimationData(mobile);
      } catch (error) {
        console.error(`Error loading slider widget "${name}":`, error);
      }
    };

    loadData();
    return () => {
      isActive = false;
    };
  }, [name, lang]);

  const ready = sceneData && animationData && translationData;

  return (
    <div className={className.sliderWidget}>
      {ready ? (
        <SliderWidgetScene
          animationData={animationData}
          mobileAnimationData={mobileAnimationData}
          sceneData={sceneData}
          stringData={translationData}
          allowOverflow={true}
          path={name}
          lang={lang}
          standalone={false}
        />
      ) : null}
    </div>
  );
}

SliderWidget.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
};

export default SliderWidget;
